// Friends view - displays user's friends list and invite link

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { siteEvents } from '../../lib/site-events.js';
import { presenceState } from '../../state/presence-state.js';

const usersIconLarge = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="64"
  height="64"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="mb-3"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
</svg>`;

// UserPlus icon SVG
const userPlusIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <line x1="19" x2="19" y1="8" y2="14" />
  <line x1="22" x2="16" y1="11" y2="11" />
</svg>`;

// Copy icon SVG
const copyIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
</svg>`;

/**
 * Friends view component
 * Displays the user's friends list and invite link for adding new friends
 */
class FriendsView extends BaseComponent {
  static properties = {
    _friends: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _inviteUrl: { type: String, state: true },
    _inviteLoading: { type: Boolean, state: true },
    _inviteError: { type: String, state: true },
    _copied: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _searchResults: { type: Array, state: true },
    _searchBusyId: { type: String, state: true },
    _pendingRequests: { type: Array, state: true },
    _requestBusyId: { type: String, state: true },
    _friendBusyId: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Friend & {email_hash?: string, created_at?: string}>} */
    this._friends = [];
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
    this._inviteUrl = '';
    this._inviteLoading = true;
    /** @type {string | null} */
    this._inviteError = null;
    this._copied = false;

    this._searchQuery = '';
    /** @type {Array<{id: string, username: string, email_hash?: string, picture?: string, status: string}>} */
    this._searchResults = [];
    this._searchBusyId = '';
    /** @type {Array<object>} */
    this._pendingRequests = [];
    this._requestBusyId = '';
    this._friendBusyId = '';
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._searchDebounce = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    Promise.all([this._loadInviteCode(), this._loadFriends(), this._loadRequests()]);
    // Live updates: when the other party accepts / declines / cancels,
    // or when an unfriend happens elsewhere, refresh both lists.
    this._unsubFriendship = siteEvents.subscribe('friendship-changed', () => {
      this._loadFriends();
      this._loadRequests();
    });
    this._unsubFriendRequest = siteEvents.subscribe('friend-request', () => {
      this._loadRequests();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubFriendship?.();
    this._unsubFriendRequest?.();
  }

  async _loadRequests() {
    try {
      this._pendingRequests = await api.listFriendRequests();
    } catch (err) {
      console.warn('[friends-view] failed to load requests', err);
      this._pendingRequests = [];
    }
  }

  /** @param {Event} e */
  _onSearchInput(e) {
    const value = /** @type {HTMLInputElement} */ (e.target).value;
    this._searchQuery = value;
    if (this._searchDebounce) clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => this._runSearch(), 200);
  }

  async _runSearch() {
    const q = this._searchQuery.trim();
    if (q.length < 2) {
      this._searchResults = [];
      return;
    }
    try {
      this._searchResults = await api.searchUsers(q);
    } catch (err) {
      console.warn('[friends-view] search failed', err);
      this._searchResults = [];
    }
  }

  /** @param {{id: string, username: string}} user */
  async _sendRequest(user) {
    this._searchBusyId = user.id;
    try {
      const result = await api.sendFriendRequest(user.id);
      // Update inline status pill on the search row.
      this._searchResults = this._searchResults.map((r) =>
        r.id === user.id
          ? {
              ...r,
              status:
                result.status === 'auto_accepted' || result.status === 'already_friends'
                  ? 'already_friends'
                  : 'request_sent',
            }
          : r,
      );
      // Refresh friends + requests so the lists below reflect reality.
      await Promise.all([this._loadFriends(), this._loadRequests()]);
    } catch (err) {
      console.warn('[friends-view] sendRequest failed', err);
    } finally {
      this._searchBusyId = '';
    }
  }

  /** @param {string} requestId */
  async _acceptRequest(requestId) {
    this._requestBusyId = requestId;
    try {
      await api.acceptFriendRequest(requestId);
      await Promise.all([this._loadFriends(), this._loadRequests()]);
    } finally {
      this._requestBusyId = '';
    }
  }

  /** @param {string} requestId */
  async _rejectRequest(requestId) {
    this._requestBusyId = requestId;
    try {
      await api.rejectFriendRequest(requestId);
      await this._loadRequests();
    } finally {
      this._requestBusyId = '';
    }
  }

  /** @param {{id: string, username?: string}} friend */
  async _removeFriend(friend) {
    if (!confirm(`Remove ${friend.username || 'this friend'} from your friends?`)) return;
    this._friendBusyId = friend.id;
    try {
      await api.removeFriend(friend.id);
      await Promise.all([this._loadFriends(), presenceState.refetch()]);
    } catch (err) {
      console.warn('[friends-view] removeFriend failed', err);
    } finally {
      this._friendBusyId = '';
    }
  }

  /** @param {string} requestId */
  async _cancelRequest(requestId) {
    this._requestBusyId = requestId;
    try {
      await api.cancelFriendRequest(requestId);
      await this._loadRequests();
    } finally {
      this._requestBusyId = '';
    }
  }

  async _loadInviteCode() {
    this._inviteLoading = true;
    this._inviteError = null;

    try {
      const data = await api.getFriendInviteCode();
      this._inviteUrl = data.url;
    } catch (err) {
      console.error('Failed to load invite code:', err);
      this._inviteError = 'Failed to load invite code. Please try again.';
    } finally {
      this._inviteLoading = false;
    }
  }

  async _loadFriends() {
    this._loading = true;
    this._error = null;

    try {
      this._friends =
        /** @type {Array<import('../../types/jsdoc-types.js').Friend & {email_hash?: string, created_at?: string}>} */ (
          await api.getFriends()
        );
    } catch (err) {
      console.error('Failed to load friends:', err);
      this._error = 'Failed to load friends. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    return `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=96`;
  }

  /** @param {string} dateString */
  _formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  async _copyInviteLink() {
    try {
      await navigator.clipboard.writeText(this._inviteUrl);
      this._copied = true;
      setTimeout(() => {
        this._copied = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: select the input text
      const input = /** @type {HTMLInputElement | null} */ (this.shadowRoot?.querySelector('#invite-link-input'));
      if (input) {
        input.select();
      }
    }
  }

  _statusPill(/** @type {string} */ status) {
    switch (status) {
      case 'already_friends':
        return html`<span class="badge bg-success">Already friends</span>`;
      case 'request_sent':
        return html`<span class="badge bg-secondary">Request sent</span>`;
      case 'request_received':
        return html`<span class="badge bg-info text-dark">Wants to friend you</span>`;
      default:
        return null;
    }
  }

  render() {
    const incoming = this._pendingRequests.filter((r) => r.direction === 'incoming');
    const outgoing = this._pendingRequests.filter((r) => r.direction === 'outgoing');

    return html`
      <!-- Find Friends Section -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Find Friends</h5>
        </div>
        <div class="card-body">
          <input
            type="text"
            class="form-control mb-3"
            placeholder="Search by username..."
            .value="${this._searchQuery}"
            @input="${this._onSearchInput}"
          />
          ${
            this._searchResults.length > 0
              ? html`
                <div class="list-group">
                  ${this._searchResults.map(
                    (r) => html`
                      <div class="list-group-item d-flex align-items-center gap-3">
                        <img src="${r.picture || this._getGravatarUrl(r.email_hash || '')}" alt="${r.username}" class="rounded-circle" style="width: 36px; height: 36px;" />
                        <div class="flex-grow-1">${r.username}</div>
                        ${
                          r.status === 'add'
                            ? html`
                              <button
                                class="btn btn-sm btn-primary"
                                ?disabled="${this._searchBusyId === r.id}"
                                @click="${() => this._sendRequest(r)}"
                              >
                                Add
                              </button>
                            `
                            : this._statusPill(r.status)
                        }
                      </div>
                    `,
                  )}
                </div>
              `
              : this._searchQuery.trim().length >= 2
                ? html`<p class="text-muted small mb-0">No matches.</p>`
                : html`<p class="text-muted small mb-0">Type at least 2 characters to search.</p>`
          }
        </div>
      </div>

      <!-- Pending Requests Section -->
      ${
        incoming.length + outgoing.length > 0
          ? html`
            <div class="card mb-4">
              <div class="card-header">
                <h5 class="mb-0">Pending Requests</h5>
              </div>
              <div class="card-body">
                ${
                  incoming.length > 0
                    ? html`
                      <h6 class="text-muted">Incoming</h6>
                      <div class="list-group mb-3">
                        ${incoming.map(
                          (r) => html`
                            <div class="list-group-item d-flex align-items-center gap-3">
                              <img src="${r.otherPicture || this._getGravatarUrl(r.otherEmailHash || '')}" alt="${r.otherUsername}" class="rounded-circle" style="width: 36px; height: 36px; object-fit: cover;" />
                              <div class="flex-grow-1">${r.otherUsername}</div>
                              <button class="btn btn-sm btn-success" ?disabled="${this._requestBusyId === r.id}" @click="${() => this._acceptRequest(r.id)}">Accept</button>
                              <button class="btn btn-sm btn-outline-secondary" ?disabled="${this._requestBusyId === r.id}" @click="${() => this._rejectRequest(r.id)}">Decline</button>
                            </div>
                          `,
                        )}
                      </div>
                    `
                    : ''
                }
                ${
                  outgoing.length > 0
                    ? html`
                      <h6 class="text-muted">Outgoing</h6>
                      <div class="list-group">
                        ${outgoing.map(
                          (r) => html`
                            <div class="list-group-item d-flex align-items-center gap-3">
                              <img src="${r.otherPicture || this._getGravatarUrl(r.otherEmailHash || '')}" alt="${r.otherUsername}" class="rounded-circle" style="width: 36px; height: 36px; object-fit: cover;" />
                              <div class="flex-grow-1">${r.otherUsername} <span class="text-muted small">— pending</span></div>
                              <button class="btn btn-sm btn-outline-danger" ?disabled="${this._requestBusyId === r.id}" @click="${() => this._cancelRequest(r.id)}">Cancel</button>
                            </div>
                          `,
                        )}
                      </div>
                    `
                    : ''
                }
              </div>
            </div>
          `
          : ''
      }

      <!-- Invite Friends Section -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">${userPlusIcon} Invite Friends</h5>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">Share this link with friends to add them to your friends list:</p>
          ${
            this._inviteLoading
              ? html`
                <div class="text-center py-3">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
              `
              : this._inviteError
                ? html` <div class="alert alert-danger mb-0">${this._inviteError}</div> `
                : html`
                  <div class="input-group">
                    <input
                      id="invite-link-input"
                      type="text"
                      class="form-control"
                      .value="${this._inviteUrl}"
                      readonly
                    />
                    <button class="btn btn-outline-primary" type="button" @click="${this._copyInviteLink}">
                      ${copyIcon} ${this._copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                `
          }
        </div>
      </div>

      <!-- Friends List Section -->
      <div>
        <h2 class="h4 mb-3">Your Friends (${this._friends.length})</h2>

        ${
          this._loading
            ? html`
              <div class="text-center text-muted py-5">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3">Loading friends...</p>
              </div>
            `
            : this._error
              ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
              : this._friends.length === 0
                ? html`
                  <div class="text-center text-muted py-5">
                    ${usersIconLarge}
                    <p>No friends yet. Share your invite link to add friends!</p>
                  </div>
                `
                : html`
                  <div class="row row-cols-1 row-cols-sm-2 row-cols-xl-3 g-3">
                    ${this._friends.map(
                      (friend) => html`
                        <div class="col">
                          <div class="card h-100">
                            <div class="card-body d-flex align-items-center gap-3">
                              <img
                                src="${friend.picture || this._getGravatarUrl(friend.email_hash || '')}"
                                alt="${friend.username}"
                                class="rounded-circle"
                                style="width: 48px; height: 48px; object-fit: cover;"
                              />
                              <div class="flex-grow-1">
                                <h6 class="card-title mb-0">${friend.username}</h6>
                                <div class="text-muted small">
                                  Friends since ${this._formatDate(friend.created_at || '')}
                                </div>
                              </div>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Remove friend"
                                aria-label="Remove ${friend.username}"
                                ?disabled="${this._friendBusyId === friend.id}"
                                @click="${() => this._removeFriend(friend)}"
                              >
                                <i class="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                `
        }
      </div>
    `;
  }
}

customElements.define('friends-view', FriendsView);
